import { watchEffect } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import { authStatus, isLoggedIn, updateAuthStatus } from "@/state/api/authState";
import useAccess from "@/composable/useAccess";
import { validToken, detectPlatform } from "@/utils";
import * as NProgress from "nprogress";

declare module "vue-router" {
	interface RouteMeta {
		requiresAuth: boolean;
		rule: string[];
	}
}

const platform = detectPlatform();

// Manual routes
const routes = [
	{
		path: "",
		redirect: { name: "auth-login" },
	},
	// Public — réservation voyageur (sans auth, hors wrapper /auth).
	// Accessible directement via /reservation. Pas de layout app interne :
	// la page se rend en plein écran avec sa propre charte light.
	{
		component: () =>
			import(/* webpackChunkName: "public-group" */ "/src/pages/public/reservation.vue"),
		name: "public-reservation",
		path: "/reservation",
		meta: {
			requiresAuth: false,
			rule: [],
			linkType: "normal",
		},
	},
	// P12.12 — Page billets publique scannée depuis le QR de la preuve
	// PDF. Affiche les billets matérialisés si la résa est confirmée,
	// ou la preuve de réservation avec compte à rebours sinon. Pas
	// d'auth requise — F7 privacy : masquage des PII par défaut, le
	// vrai propriétaire déverrouille en passant ?phone=6XXXXXXXX.
	{
		component: () => import(/* webpackChunkName: "public-group" */ "/src/pages/public/voyage.vue"),
		name: "public-voyage",
		path: "/voyage/:uuid",
		meta: {
			requiresAuth: false,
			rule: [],
			linkType: "normal",
		},
	},
	// Auth
	{
		component: () => import(/* webpackChunkName: "auth-group" */ "/src/pages/auth.vue"),
		name: "auth",
		path: "/auth",
		props: true,
		children: [
			{
				path: "",
				redirect: { name: "auth-login" },
			},
			{
				component: () => import(/* webpackChunkName: "auth-group" */ "/src/pages/auth/login.vue"),
				name: "auth-login",
				path: "login",
				props: true,
				meta: {
					requiresAuth: false,
					rule: [],
					linkType: "normal",
				},
			},
			{
				component: () => import(/* webpackChunkName: "auth-group" */ "/src/pages/auth/logout.vue"),
				name: "auth-logout",
				path: "logout/:action?",
				props: true,
				meta: {
					requiresAuth: false,
					rule: [],
					linkType: "normal",
				},
			},
			{
				component: () =>
					import(/* webpackChunkName: "auth-group" */ "/src/pages/auth/blocked-page.vue"),
				name: "auth-blocked",
				path: "forbidden/:mode?",
				props: true,
				meta: {
					requiresAuth: false,
					rule: [],
					linkType: "normal",
				},
			},
			{
				component: () =>
					import(/* webpackChunkName: "auth-group" */ "/src/pages/auth/register.vue"),
				name: "auth-register",
				path: "register",
				props: true,
				meta: {
					requiresAuth: false,
					rule: [],
					linkType: "normal",
				},
			},
			{
				component: () =>
					import(/* webpackChunkName: "auth-group" */ "/src/pages/auth/forgot-password.vue"),
				name: "auth-forgot-password",
				path: "forgot-password",
				props: true,
				meta: {
					requiresAuth: false,
					rule: [],
					linkType: "normal",
				},
			},
			{
				component: () =>
					import(/* webpackChunkName: "auth-group" */ "/src/pages/auth/validate.vue"),
				name: "auth-validate",
				path: "validate/:id/:crsf",
				props: true,
				meta: {
					requiresAuth: false,
					rule: [],
					linkType: "normal",
				},
			},
			// {
			// 	component: () =>
			// 		import(/* webpackChunkName: "auth-group" */ "/src/pages/auth/condition-of-use.vue"),
			// 	name: "auth-condition-of-use",
			// 	path: "condition-of-use",
			// 	props: false,
			// 	meta: {
			// 		requiresAuth: false,
			// 		rule: [],
			// 		linkType: "normal",
			// 	},
			// },
			// {
			// 	component: () =>
			// 		import(/* webpackChunkName: "auth-group" */ "/src/pages/auth/privacy-policy.vue"),
			// 	name: "auth-privacyPolicy",
			// 	path: "privacy-policy",
			// 	props: false,
			// 	meta: {
			// 		requiresAuth: false,
			// 		rule: [],
			// 		linkType: "normal",
			// 	},
			// },
		],
	},
	// Workspace
	{
		component: () => import(/* webpackChunkName: "dashboard-group" */ "/src/pages/workspace.vue"),
		name: "workspace",
		path: "/workspace",
		props: true,
		children: [
			{
				path: "",
				redirect: { name: "workspace-dashboard" },
			},
			// Dashboard
			{
				component: () =>
					import(
						/* webpackChunkName: "dashboard-group" */ "/src/pages/workspace/dashboards/Overview.vue"
					),
				name: "workspace-dashboard",
				path: "dashboard",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["all"],
					feature: "feature.dashboards.main",
				},
			},
			// Recherche globale (barre du haut). Pas de feature gate :
			// infrastructure transverse accessible à tous les rôles.
			{
				component: () =>
					import(
						/* webpackChunkName: "search-group" */ "/src/pages/workspace/RechercheGlobale.vue"
					),
				name: "workspace-recherche",
				path: "recherche",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["all"],
				},
			},
			// Investor Dashboard (Root + Admin only)
			{
				component: () =>
					import(
						/* webpackChunkName: "dashboard-group" */ "/src/pages/workspace/dashboards/InvestorDashboard.vue"
					),
				name: "workspace-investor-dashboard",
				path: "investor-dashboard",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["investor-dashboard"],
					feature: "feature.dashboards.investor",
				},
			},
			// Tax Dashboard (Root + Admin only)
			{
				component: () =>
					import(
						/* webpackChunkName: "dashboard-group" */ "/src/pages/workspace/dashboards/TaxDashboard.vue"
					),
				name: "workspace-tax-dashboard",
				path: "tax-dashboard",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["tax-dashboard"],
					feature: "feature.dashboards.tax",
				},
			},
			// KPI Dashboard
			{
				component: () =>
					import(
						/* webpackChunkName: "dashboard-group" */ "/src/pages/workspace/dashboards/KpiDashboard.vue"
					),
				name: "workspace-kpi-dashboard",
				path: "kpi-dashboard",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["kpi"],
					feature: "feature.dashboards.kpi", // bloqué par featureGuard si OFF
				},
			},
			// Agency
			{
				component: () =>
					import(/* webpackChunkName: "agencies-group" */ "/src/layouts/SubLayout.vue"),
				name: "workspace-agency",
				path: "agency",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["station", "retrieve-list-stations-only"],
					feature: "module.agencies",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-agency-overview" },
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "agencies-group" */ "/src/pages/workspace/agencies/AgencyOverview.vue"
							),
						name: "workspace-agency-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["station", "retrieve-list-stations-only"],
							feature: "feature.agencies.overview",
						},
					},
				],
			},
			// Operation
			{
				component: () =>
					import(/* webpackChunkName: "operation-group" */ "/src/layouts/SubLayout.vue"),
				name: "workspace-operations",
				path: "operations",
				props: true,
				meta: {
					requiresAuth: true,
					rule: [],
					feature: "module.operations",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-operation-overview" },
					},
					// operations-overview
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/OperationOverview.vue"
							),
						name: "workspace-operations-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: [
								"passenger",
								"passenger-baggage",
								"package",
								"package-pickup",
								"package-transfer",
								"expense",
							],
							feature: "feature.operations.overview",
						},
					},
					// operations-new-passenger
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/NewPassenger.vue"
							),
						name: "workspace-operations-new-passenger",
						path: "new-passenger",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["passenger"],
							feature: "feature.operations.passengers",
						},
					},
					// operations-passenger-baggage
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/PassengerBaggages.vue"
							),
						name: "workspace-operations-passenger-baggage",
						path: "passenger-baggage",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["passenger-baggage"],
							feature: "feature.operations.baggages",
						},
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/PackagesRegistration.vue"
							),
						name: "workspace-operations-package-registration",
						path: "package-registration",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["package"],
							feature: "feature.operations.packages",
						},
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/PackagesControl.vue"
							),
						name: "workspace-operations-package-control",
						path: "package-control",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["package"],
							feature: "feature.operations.package_control",
						},
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/PackagesPickup.vue"
							),
						name: "workspace-operations-package-pickup",
						path: "package-pickup",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["package-pickup"],
							feature: "feature.operations.package_pickup",
						},
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/PackagesLoading.vue"
							),
						name: "workspace-operations-package-loading",
						path: "package-loading",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["package"],
							feature: "feature.operations.qrcode_scan",
						},
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/PackagesHomeDelivery.vue"
							),
						name: "workspace-operations-package-delivery",
						path: "package-delivery",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["package-pickup"],
							feature: "feature.operations.qrcode_scan",
						},
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/PackageTrackingView.vue"
							),
						name: "workspace-operations-package-tracking",
						path: "package-tracking",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["package"],
							feature: "feature.operations.packages",
						},
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/ExpenseManagement.vue"
							),
						name: "workspace-operations-expense-management",
						path: "expense-management",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["expense"],
							feature: "feature.operations.expenses",
						},
					},
					// Ma caisse (caissier individuel) — pas de feature flag,
					// route core (cf. menu.ts pour la justification).
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/MyCashierSession.vue"
							),
						name: "workspace-my-cashier-session",
						path: "my-cashier-session",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["cashier-session"],
						},
					},
					// Audit MainCashier / Admin — idem, pas de feature gate.
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/CashierSessionsManagement.vue"
							),
						name: "workspace-cashier-sessions-management",
						path: "cashier-sessions",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["cashier-session"],
						},
					},
					// reservations
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/ReservationsManagement.vue"
							),
						name: "workspace-operations-reservations",
						path: "reservations",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["reservation"],
							feature: "feature.operations.reservations",
						},
					},
					// refunds
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/RefundsManagement.vue"
							),
						name: "workspace-operations-refunds",
						path: "refunds",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["ticket"],
							feature: "feature.operations.refunds",
						},
					},
					// fleet live tracking
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/FleetTracking.vue"
							),
						name: "workspace-operations-fleet-tracking",
						path: "fleet-tracking",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["vehicle"],
							feature: "feature.tracking.fleet_live",
						},
					},
					// PR-9 : Contrôle d'embarquement avec scan QR. Gardé par le
					// même feature flag que les opérations passagers + qrcode_scan.
					{
						component: () =>
							import(
								/* webpackChunkName: "operations-group" */ "/src/pages/workspace/operations/BoardingControl.vue"
							),
						name: "workspace-operations-boarding-control",
						path: "boarding-control",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["passenger"],
							feature: "feature.operations.qrcode_scan",
						},
					},
				],
			},
			// Auto Park
			{
				component: () =>
					/* webpackChunkName: "autopack-group" */ import("/src/layouts/SubLayout.vue"),
				name: "workspace-autopark",
				path: "autopark",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["expedition", "vehicle", "driver", "technical-visit", "maintenance"],
					feature: "module.autopark",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-autopark-overview" },
					},
					// autopark-overview
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/AutoparkOverview.vue"
							),
						name: "workspace-autopark-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["expedition", "vehicle", "driver", "technical-visit", "maintenance"],
							feature: "feature.autopark.overview",
						},
					},
					// autopark-vehicle
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/VehicleAutopark.vue"
							),
						name: "workspace-autopark-vehicle",
						path: "vehicle",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["vehicle"],
							feature: "feature.autopark.vehicles",
						},
					},
					// autopark-technical-visit
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/TechnicalVisitAutopark.vue"
							),
						name: "workspace-autopark-technical-visit",
						path: "technical-visit",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["technical-visit"],
							feature: "feature.autopark.technical_visits",
						},
					},
					// Remove it
					// {
					// 	component: () => import(/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/ProvidersAutopark.vue"),
					// 	name: "workspace-autopark-providers",
					// 	path: "providers",
					// 	props: true,
					// 	meta: {
					// 		requiresAuth: true,
					// 		rule: [],
					// 	}
					// },
					// autopark-beverage - Consommation Carburant
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/BeverageAutopark.vue"
							),
						name: "workspace-autopark-beverage",
						path: "beverage", // Consommation Carburant
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["beverage"],
							feature: "feature.autopark.fuel_consumption",
						},
					},
					// autopark-maintenance
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/MaintenanceAutopark.vue"
							),
						name: "workspace-autopark-maintenance",
						path: "maintenance", // Entretien
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["maintenance"],
							feature: "feature.autopark.maintenance",
						},
					},
					// autopark-insurance
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/InsuranceAutopark.vue"
							),
						name: "workspace-autopark-insurance",
						path: "insurance",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["insurance"],
							feature: "feature.autopark.insurance",
						},
					},
					// vehicle-tax
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/VehicleTaxAutopark.vue"
							),
						name: "workspace-autopark-vehicle-tax",
						path: "vehicle-tax",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["vehicle-tax"],
							feature: "feature.autopark.vehicle_tax",
						},
					},
					// vehicle-sale
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/SaleAutopark.vue"
							),
						name: "workspace-autopark-vehicle-sale",
						path: "vehicle-sale",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["vehicle-sale"],
							feature: "feature.autopark.vehicle_sales",
						},
					},
					// vehicle-rental
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/RentalAutopark.vue"
							),
						name: "workspace-autopark-vehicle-rental",
						path: "vehicle-rental",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["vehicle-rental"],
							feature: "feature.autopark.vehicle_rentals",
						},
					},
					// expiration alerts
					{
						component: () =>
							import(
								/* webpackChunkName: "autopack-group" */ "/src/pages/workspace/autopark/ExpirationAlerts.vue"
							),
						name: "workspace-autopark-expiration-alerts",
						path: "expiration-alerts",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["vehicle"],
							feature: "feature.autopark.expiration_alerts",
						},
					},
				],
			},
			// Human Resources
			{
				component: () =>
					/* webpackChunkName: "human-resources-group" */ import("/src/layouts/SubLayout.vue"),
				name: "workspace-human-resources",
				path: "human-resources",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["station-staff", "salary"],
					feature: "module.human_resources",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-human-resources-overview" },
					},
					// human-resources-overview
					{
						component: () =>
							import(
								/* webpackChunkName: "human-resources-group" */ "/src/pages/workspace/human-resources/HumanResourcesOverview.vue"
							),
						name: "workspace-human-resources-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["station-staff", "salary"],
							feature: "feature.hr.overview",
						},
					},
					// human-resources-staff
					{
						component: () =>
							import(
								/* webpackChunkName: "human-resources-group" */ "/src/pages/workspace/human-resources/StaffHumanResources.vue"
							),
						name: "workspace-human-resources-staff",
						path: "staff",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["station-staff"],
							feature: "feature.hr.staff",
						},
					},
					// human-resources-salary
					{
						component: () =>
							import(
								/* webpackChunkName: "human-resources-group" */ "/src/pages/workspace/human-resources/SalaryHumanResources.vue"
							),
						name: "workspace-human-resources-salary",
						path: "salary",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["salary"],
							feature: "feature.hr.salary_payroll",
						},
					},
					// human-resources-leave
					{
						component: () =>
							import(
								/* webpackChunkName: "human-resources-group" */ "/src/pages/workspace/human-resources/LeaveHumanResources.vue"
							),
						name: "workspace-human-resources-leave",
						path: "leave",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["leave"],
							feature: "feature.hr.leave_management",
						},
					},
					// human-resources-bonus
					{
						component: () =>
							import(
								/* webpackChunkName: "human-resources-group" */ "/src/pages/workspace/human-resources/BonusHumanResources.vue"
							),
						name: "workspace-human-resources-bonus",
						path: "bonus",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["bonus"],
							feature: "feature.hr.bonus_management",
						},
					},
				],
			},
			// Planning
			{
				component: () =>
					/* webpackChunkName: "planning-group" */ import("/src/layouts/SubLayout.vue"),
				name: "workspace-planning",
				path: "planning",
				props: true,
				meta: {
					requiresAuth: true,
					rule: [],
					feature: "module.planning",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-planning-overview" },
					},
					// planning-overview
					{
						component: () =>
							import(
								/* webpackChunkName: "planning-group" */ "/src/pages/workspace/planning/PlanningOverview.vue"
							),
						name: "workspace-planning-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["expedition", "embarkation", "travel-route", "ticket-rate"],
							feature: "feature.planning.overview",
						},
					},
					// planning-embarkation
					{
						component: () =>
							import(
								/* webpackChunkName: "planning-group" */ "/src/pages/workspace/planning/EmbarkationPlanning.vue"
							),
						name: "workspace-planning-embarkation",
						path: "embarkation",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["embarkation"],
							feature: "feature.planning.embarkations",
						},
					},
					// planning-embarkation
					{
						component: () =>
							import(
								/* webpackChunkName: "planning-group" */ "/src/pages/workspace/planning/ExpeditionPlanning.vue"
							),
						name: "workspace-planning-expedition",
						path: "expedition",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["expedition"],
							feature: "feature.planning.expeditions",
						},
					},
					// planning-others
					{
						component: () =>
							import(
								/* webpackChunkName: "planning-group" */ "/src/pages/workspace/planning/OthersPlanning.vue"
							),
						name: "workspace-planning-others",
						path: "others",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["contract-type", "responsability-function", "payment-method"],
							feature: "feature.planning.others_config",
						},
					},
					// planning-routes-and-rates
					{
						component: () =>
							import(
								/* webpackChunkName: "planning-group" */ "/src/pages/workspace/planning/RoutesAndRatesPlanning.vue"
							),
						name: "workspace-planning-routes-and-rates",
						path: "routes-and-rates",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["travel-route", "ticket-rate"],
							feature: "feature.planning.routes_rates",
						},
					},
					// manifest — disabled pending modernization of the planning flow
					/*
					{
						component: () =>
							import(
								"/src/pages/workspace/planning/ManifestView.vue"
							),
						name: "workspace-planning-manifest",
						path: "manifest",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["embarkation"],
						feature: "feature.planning.travel_manifest",
						},
					},
					*/
					// bordereau de transfert
					{
						component: () =>
							import(
								/* webpackChunkName: "planning-group" */ "/src/pages/workspace/planning/BordereauView.vue"
							),
						name: "workspace-planning-bordereau",
						path: "bordereau",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["package-transfer", "expedition"],
							feature: "feature.planning.transfer_bordereau",
						},
					},
				],
			},
			// Providers
			{
				component: () =>
					/* webpackChunkName: "provider-group" */ import("/src/layouts/SubLayout.vue"),
				name: "workspace-providers",
				path: "providers",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["providers"],
					feature: "module.providers",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-providers-overview" },
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "provider-group" */ "/src/pages/workspace/providers/ProvidersOverview.vue"
							),
						name: "workspace-providers-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["providers"],
							feature: "feature.providers.overview",
						},
					},
				],
			},
			// Administration
			{
				component: () => /* webpackChunkName: "admin-group" */ import("/src/layouts/SubLayout.vue"),
				name: "workspace-administration",
				path: "administration",
				props: true,
				meta: {
					requiresAuth: true,
					rule: [],
					feature: "module.administration",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-administration-overview" },
					},
					// overview
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/AdminOverview.vue"
							),
						name: "workspace-administration-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["permission", "transaction-report"],
							feature: "feature.admin.users",
						},
					},
					// passnengers transferred
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/PassengersTransferredReport.vue"
							),
						name: "workspace-administration-passengers-transferred", // Bordereau d'expedition
						path: "passengers-transferred", // Bordereau de route
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
							feature: "feature.operations.passengers",
						},
					},
					// discount granted
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/DiscountsGrantedReport.vue"
							),
						name: "workspace-administration-discounts-granted", // Bordereau d'expedition
						path: "discounts-granted", // Bordereau de route
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
							feature: "feature.accounting.transaction_history",
						},
					},
					// users
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/UsersManagement.vue"
							),
						name: "workspace-administration-users",
						path: "users",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["user"],
							feature: "feature.admin.users",
						},
					},
					// permissions
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/PermissionsManagement.vue"
							),
						name: "workspace-administration-permissions",
						path: "permissions",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["permission"],
							feature: "feature.admin.permissions",
						},
					},
					// roles
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/RolesManagement.vue"
							),
						name: "workspace-administration-roles",
						path: "roles",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["role"],
							feature: "feature.admin.roles",
						},
					},
					// vehicle types
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/VehicleTypesManagement.vue"
							),
						name: "workspace-administration-vehicle-types",
						path: "vehicle-types",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["vehicle-type"],
							feature: "feature.admin.vehicle_types",
						},
					},
					// Feature definitions registry (Root-only)
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/FeatureDefinitionsManagement.vue"
							),
						name: "workspace-administration-feature-definitions",
						path: "feature-definitions",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["enterprise"],
							feature: "feature.admin.feature_management",
						},
					},
					// Catalogue tarifaire — souscriptions (Root-only)
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/PricingCatalogManagement.vue"
							),
						name: "workspace-administration-pricing-catalog",
						path: "pricing-catalog",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["enterprise"],
							feature: "feature.admin.pricing_catalog",
						},
					},
					// Liste Contrats (Root-only) — pilotage des activations
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/ContractsManagement.vue"
							),
						name: "workspace-administration-contracts",
						path: "contracts",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["enterprise"],
							feature: "feature.admin.subscriptions",
						},
					},
					// Période d'essai (Root-only) — ouverture sur tenant existant
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/TrialManagement.vue"
							),
						name: "workspace-administration-trial",
						path: "trial",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["enterprise"],
							feature: "feature.admin.subscriptions",
						},
					},
					// Multi-agency feature flags admin (Admin entreprise)
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/FeatureFlagsManagement.vue"
							),
						name: "workspace-administration-feature-flags",
						path: "feature-flags",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["enterprise"],
							feature: "feature.admin.feature_management",
						},
					},
					// Regions
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/RegionsManagement.vue"
							),
						name: "workspace-administration-regions",
						path: "regions",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["region"],
							feature: "feature.admin.regions",
						},
					},
					// Enterprise
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/EnterpriseManagement.vue"
							),
						name: "workspace-administration-enterprise",
						path: "enterprise",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["enterprise"],
							feature: "feature.admin.enterprises",
						},
					},
					// Template
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/TemplateManagement.vue"
							),
						name: "workspace-administration-template",
						path: "templates",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["templates"],
							feature: "feature.admin.templates",
						},
					},
					// Enterprise Features — toggle GLOBAL par module (`module.*`).
					// Vue d'ensemble pour Root : choix entreprise + on/off de
					// chaque module. La configuration fine (`feature.*`) est
					// dans la route séparée `workspace-administration-feature-flags`.
					{
						component: () =>
							import(
								/* webpackChunkName: "admin-group" */ "/src/pages/workspace/administration/EnterpriseFeatureManagement.vue"
							),
						name: "workspace-administration-features",
						path: "features",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["enterprise"],
							feature: "feature.admin.feature_management",
						},
					},
				],
			},
			// Files
			{
				component: () => /* webpackChunkName: "admin-group" */ import("/src/layouts/SubLayout.vue"),
				name: "workspace-files",
				path: "files",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["file"],
					feature: "module.files",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-files-overview" },
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "agencies-group" */ "/src/pages/workspace/files/FilesOverview.vue"
							),
						name: "workspace-files-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["file"],
							feature: "feature.files.upload",
						},
					},
				],
			},
			// Report
			{
				component: () =>
					/* webpackChunkName: "report-group" */ import("/src/layouts/SubLayout.vue"),
				name: "workspace-report",
				path: "report",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["travel-docket", "route-docket"],
					feature: "module.reports",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-report-overview" },
					},
					// report-overview
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/ReportsOverview.vue"
							),
						name: "workspace-report-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["travel-docket", "route-docket"],
							feature: "feature.reports.overview",
						},
					},
					// report-travel-dockets
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/TravelDocketsReport.vue"
							),
						name: "workspace-report-travel-dockets",
						path: "travel-dockets", // Bordereau de voyage
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["travel-docket"],
							feature: "feature.reports.travel_docket",
						},
					},
					// report-shipping-dockets (Bordereaux d'expedition)
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/ShippingDocketsReport.vue"
							),
						name: "workspace-report-shipping-dockets",
						path: "shipping-dockets",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["shipping-docket"],
							feature: "feature.reports.shipping_docket",
						},
					},
					// report-route-dockets (Bordereaux de route)
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/RouteDocketsReport.vue"
							),
						name: "workspace-report-route-dockets",
						path: "route-dockets",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["route-docket"],
							feature: "feature.reports.route_docket",
						},
					},
					// rapport des recettes
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/ReceiptsReport.vue"
							),
						name: "workspace-report-receipts-report", // Rapport des Recettes
						path: "receipts-report",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
							feature: "feature.accounting.receipts_report", // route-dockets
						},
					},
					// rapport des depenses
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/ExpensesReport.vue"
							),
						name: "workspace-report-expenses-report", // Rapport des Recettes
						path: "expenses-report",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
							feature: "feature.accounting.expenses_report", // route-dockets
						},
					},
					// cash register
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/CashRegister.vue"
							),
						name: "workspace-report-cash-register",
						path: "cash-register",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
							feature: "feature.accounting.cash_register",
						},
					},
					// reconciliation
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/ReconciliationReport.vue"
							),
						name: "workspace-report-reconciliation",
						path: "reconciliation",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
							feature: "feature.accounting.reconciliation",
						},
					},
					// bank deposits (versements en banque)
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/BankDeposits.vue"
							),
						name: "workspace-report-bank-deposits",
						path: "bank-deposits",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
							feature: "feature.accounting.bank_deposits",
						},
					},
					// alertes & surveillance (réservé aux admins — gate API)
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/Alerts.vue"
							),
						name: "workspace-report-alerts",
						path: "alerts",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
						},
					},
					// scheduled email reports
					{
						component: () =>
							import(
								/* webpackChunkName: "report-group" */ "/src/pages/workspace/reports/ScheduledReports.vue"
							),
						name: "workspace-report-scheduled",
						path: "scheduled",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["transaction-report"],
							feature: "feature.reports.scheduled_reports",
						},
					},
				],
			},
			// Settings
			{
				component: () =>
					/* webpackChunkName: "settings-group" */ import("/src/layouts/SubLayout.vue"),
				name: "workspace-settings",
				path: "settings",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["settings"],
					feature: "module.settings",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-settings-overview" },
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "settings-group" */ "/src/pages/workspace/settings/SettingsOverview.vue"
							),
						name: "workspace-settings-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["settings"],
							feature: "module.settings",
						},
					},
					// Mon Abonnement (Admin entreprise)
					{
						component: () =>
							import(
								/* webpackChunkName: "settings-group" */ "/src/pages/workspace/settings/MySubscription.vue"
							),
						name: "workspace-settings-my-subscription",
						path: "my-subscription",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["all"],
							linkType: "normal",
						},
					},
				],
			},
			// Profile
			{
				component: () =>
					import(/* webpackChunkName: "profile-group" */ "/src/layouts/SubLayout.vue"),
				name: "workspace-profile",
				path: "profile",
				props: true,
				meta: {
					requiresAuth: true,
					rule: [],
					feature: "module.profile",
				},
				children: [
					{
						path: "",
						redirect: { name: "workspace-profile-overview" },
					},
					{
						component: () =>
							import(
								/* webpackChunkName: "profile-group" */ "/src/pages/workspace/profile/ProfileOverview.vue"
							),
						name: "workspace-profile-overview",
						path: "overview",
						props: true,
						meta: {
							requiresAuth: true,
							rule: ["user-profile"],
							feature: "feature.profile.view",
						},
					},
				],
			},
		],
	},
	// Error
	{
		component: () => import(/* webpackChunkName: "errors-group" */ "/src/pages/error.vue"),
		name: "error",
		path: "/error",
		props: true,
		meta: {
			requiresAuth: false,
			rule: [],
		},
		children: [
			{
				path: "",
				redirect: { name: "error-code" },
			},
			{
				component: () =>
					import(/* webpackChunkName: "errors-group" */ "/src/pages/error/error.vue"),
				name: "error-code",
				path: "code/:code",
				props: true,
				meta: {
					requiresAuth: false,
					rule: [],
					linkType: "normal",
				},
			},
			// Page de blocage feature/module — affichée par featureGuard
			// quand un module ou une feature est désactivé pour l'entreprise.
			// rule: ["all"] = aucune restriction de ressource côté permissions,
			// mais l'utilisateur doit être authentifié (cf. useAccess.ts:51-54
			// qui short-circuite à true sur "all"). Sans ce sentinel, l'auth
			// guard à index.ts:1432-1434 redirige toute route avec rule:[]
			// vers /error/code/403.2, court-circuitant la page contextuelle.
			{
				component: () =>
					import(/* webpackChunkName: "errors-group" */ "/src/pages/error/ModuleDisabled.vue"),
				name: "module-disabled",
				path: "module-disabled",
				props: true,
				meta: {
					requiresAuth: true,
					rule: ["all"],
					linkType: "normal",
				},
			},
		],
	},
	// Test
	{
		component: () => import("/src/pages/test.vue"),
		name: "test",
		path: "/test",
		props: true,
		meta: {
			requiresAuth: false,
			rule: [],
			linkType: "normal",
		},
	},
	// Any
	{
		path: "/:pathMatch(.*)",
		redirect: "/error/code/404",
	},
];

const router = createRouter({
	history: createWebHistory(),
	routes, // manually set routes
});

// Feature flag guard (cascade Enterprise→Station). Runs BEFORE the
// existing auth guard so a forbidden feature short-circuits early.
// `setupFeatureWatcher` complète le guard en redirigeant automatiquement
// l'utilisateur si le module de la route courante est désactivé via
// broadcast Reverb pendant qu'il est déjà sur la page.
import { featureGuard, setupFeatureWatcher } from "./featureGuard";
router.beforeEach(featureGuard);
setupFeatureWatcher(router);

router.beforeEach((routeTo, routeFrom, next) => {
	let stopWatch1: any = null;
	let stopWatch2: any = null;
	let stopWatch3: any = null;

	// Handle NProgress display on page changes
	NProgress.start();

	// Global function
	function redirectToLogin() {
		// Pass the original route to the login component
		next({ name: "auth-login", query: { redirect: routeTo.fullPath } });
	}

	function navigation() {
		// Verify if user logged in
		if (isLoggedIn.value == true) {
			if (routeTo.name === "auth-login" || routeTo.name === "auth-register") {
				next("/workspace/dashboard");
			} else {
				next();
			}
		} else {
			next();
		}
	}

	function nextAccess() {
		if (routeTo.meta.rule.length > 0) {
			console.log("useAccess ", useAccess("resource", routeTo.meta.rule), routeTo.meta.rule);
			if (useAccess("resource", routeTo.meta.rule) == true) {
				if (useAccess("subscription", []) == true && useAccess("payment", []) == true) {
					next();
				} else {
					next("/workspace/dashboard"); // a personnaliser pour la page de paiement
				}
			} else {
				next("/error/code/403.2");
			}
		} else {
			next("/error/code/403.2");
		}
	}

	function authenticatedNavigation() {
		// If auth is required and the user is logged in...
		if (isLoggedIn.value == true) {
			const token = validToken();
			if (token.status === "valid") {
				stopWatch1 = watchEffect(() => {
					if (
						authStatus.value != "initial" &&
						authStatus.value != "disconnected" &&
						authStatus.value != "refreshing" &&
						authStatus.value != "refreshed"
					) {
						// Go to route
						nextAccess();
						// Stop watch effect
						if (stopWatch1 != null) stopWatch1();
					}
				});
			} else if (token.status === "invalid") {
				// Force user disconection
				next("/auth/logout");
			} else {
				updateAuthStatus("initial");
				redirectToLogin();
			}
		} else {
			// If auth is required and the user is NOT currently logged in,
			// redirect to login.
			redirectToLogin();
		}
	}

	// Check if auth is required on this route
	// (including nested routes).
	const authRequired = routeTo.matched.some((route) => route.meta.requiresAuth);

	// If auth isn't required for the route, just continue.
	if (!authRequired) {
		if (authStatus.value == "loading") {
			stopWatch2 = watchEffect(() => {
				if (authStatus.value != "loading") {
					// Navigate to next page
					navigation();
					// Stop watch effect
					if (stopWatch2 != null) stopWatch2();
				}
			});
		} else {
			navigation();
		}
	} else {
		// If auth loading, wait to change
		if (authStatus.value == "loading") {
			stopWatch3 = watchEffect(() => {
				if (authStatus.value != "loading") {
					// Navigate to next authenticated page
					authenticatedNavigation();
					// Stop watch effect
					if (stopWatch3 != null) stopWatch3();
				}
			});
		} else {
			authenticatedNavigation();
		}
	}
});

router.afterEach(() => {
	NProgress.done();

	// Remove splashcreen
	const appLoading = document.getElementById("splashcreen");
	if (appLoading) {
		appLoading.style.display = "none";
	}
});

export default router;
